import { useProfile } from "../context/ProfileContext";
import { GraduationCap, Trophy, BookOpen, Users } from "lucide-react";

export default function Profile() {
  const PROFILE = useProfile();
  return (
    <section id="profile" className="relative bg-[#0A0A0A] py-24 sm:py-32 border-t border-white/10" data-testid="profile-section">
      <div className="max-w-screen-2xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#E60000] font-bold mb-4">
              02 — Athlete + Student
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase font-bold tracking-tight text-white leading-none">
              On Court. <span className="text-[#8A8A8A]">In the Classroom.</span>
            </h2>
          </div>
        </div>

        {/* Bento Grid: image span 2 rows + cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 lg:grid-rows-2 gap-4 sm:gap-6">
          {/* Big image */}
          <div className="lg:row-span-2 relative overflow-hidden border border-white/10 group">
            <img
              src={PROFILE.images.action}
              alt="Faith Ahyi-Sena"
              className="w-full h-full object-cover object-top transition-all duration-700 grayscale group-hover:grayscale-0"
              style={{ minHeight: 480 }}
              data-testid="profile-action-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#E60000] font-bold mb-1">
                A5 17-2 Angel · #31
              </div>
              <div className="font-display text-2xl uppercase text-white tracking-tight">
                Middle Blocker · 6'1"
              </div>
            </div>
          </div>

          {/* Academic Card */}
          <div className="lg:col-span-2 border border-white/10 p-8 lg:p-10 bg-transparent hover:bg-[#111111] transition-colors" data-testid="academic-card">
            <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <GraduationCap className="text-[#E60000]" size={22} strokeWidth={2} />
                <span className="text-[10px] tracking-[0.3em] uppercase text-white font-bold">
                  Academic Profile
                </span>
              </div>
              <span className="text-[10px] tracking-[0.2em] uppercase text-[#8A8A8A]">
                Westminster Schools · Atlanta
              </span>
            </div>

            <div className="grid grid-cols-3 gap-6">
              <div>
                <div className="font-display text-6xl lg:text-7xl font-black text-white tracking-tighter leading-none" data-testid="stat-gpa">
                  {PROFILE.gpa}
                </div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#8A8A8A] mt-3">GPA</div>
              </div>
              <div>
                <div className="font-display text-6xl lg:text-7xl font-black text-white tracking-tighter leading-none" data-testid="stat-act">
                  {PROFILE.act}
                </div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#8A8A8A] mt-3">ACT</div>
              </div>
              <div>
                <div className="font-display text-3xl lg:text-4xl font-bold text-[#E60000] tracking-tight leading-tight" data-testid="stat-major">
                  Psychology
                </div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#8A8A8A] mt-3">Intended Major</div>
              </div>
            </div>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-8 pt-8 border-t border-white/10">
              <div data-testid="ap-classes">
                <div className="flex items-center gap-2 mb-4">
                  <BookOpen size={14} className="text-[#E60000]" strokeWidth={2.5} />
                  <span className="text-[10px] tracking-[0.3em] uppercase text-white font-bold">
                    AP Classes Taken
                  </span>
                </div>
                <ul className="space-y-2">
                  {PROFILE.apClasses.map((c) => (
                    <li key={c} className="flex items-start gap-3 text-sm text-[#cfcfcf] leading-snug">
                      <span className="text-[#E60000] mt-1 shrink-0">▸</span>
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div data-testid="extracurriculars">
                <div className="flex items-center gap-2 mb-4">
                  <Users size={14} className="text-[#E60000]" strokeWidth={2.5} />
                  <span className="text-[10px] tracking-[0.3em] uppercase text-white font-bold">
                    Extracurricular Activities
                  </span>
                </div>
                <ul className="space-y-2">
                  {PROFILE.extracurriculars.map((c) => (
                    <li key={c} className="flex items-start gap-3 text-sm text-[#cfcfcf] leading-snug">
                      <span className="text-[#E60000] mt-1 shrink-0">▸</span>
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Awards Card */}
          <div className="lg:col-span-2 border border-white/10 p-8 lg:p-10 bg-transparent hover:bg-[#111111] transition-colors" data-testid="awards-card">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Trophy className="text-[#E60000]" size={22} strokeWidth={2} />
                <span className="text-[10px] tracking-[0.3em] uppercase text-white font-bold">
                  Honors & Accolades
                </span>
              </div>
            </div>

            <ul className="divide-y divide-white/10">
              {PROFILE.awards.map((a) => (
                <li key={`${a.year}-${a.title}`} className="py-4 flex items-baseline justify-between gap-6">
                  <div className="flex items-baseline gap-6 min-w-0">
                    <span className="font-display text-2xl text-[#E60000] font-bold tracking-tighter shrink-0">
                      {a.year}
                    </span>
                    <span className="font-display text-xl sm:text-2xl uppercase font-bold tracking-tight text-white">
                      {a.title}
                    </span>
                  </div>
                  {a.note && (
                    <span className="hidden sm:inline text-xs tracking-[0.2em] uppercase text-[#8A8A8A] text-right">
                      {a.note}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
